import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import React, { ComponentType, PropsWithChildren, useEffect } from 'react';
import { createUseStyles } from '../hooks/jss';
import classnames from 'classnames';
import { useAdvancedApmRum, withAdvancedApmRum } from '@wsb/gasket-advanced-apm-rum/client';
import { IntlKeys } from '../constants/IntlKeys';
import { useIntl } from 'react-intl';
import { withLocale } from '../utils/hoc/withLocale';
import { Button } from '../components/uxCore/Button';
import { withGasketDataProvider } from '@gasket/nextjs';
import { useRouter, withRoutes } from '@wsb/gasket-plugin-complex-nextjs-routes';
import { useAnalyticsTracker } from '../hooks/useAnalyticsTracker';
import { withTheme } from '../utils/hoc/withTheme';
import { withAnalyticsTracker } from '../utils/hoc/withAnalyticsTracker';
import { EVENT_KEYS } from '../constants/analyticsEvents';
import { makeAppUrl } from '../utils/makeAppUrl';
import { useSelector } from '../hooks/useSelector';
import { PageTitle } from '../components/common/PageTitle';

const useStyles = createUseStyles(theme => ({
  home: {
    display: 'flex',
    width: '100%',
    height: '80vh',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column'
  },
  container: {
    width: '100%',
    border: `1px solid ${theme.ux.box.borderColor}`,
    backgroundColor: '#FFFFFF',
    padding: '7rem 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ['@media (max-width: 992px)']: {
      padding: '5rem 15px'
    }
  },
  myProductsLink: {
    marginTop: '1rem !important'
  }
}));

type Props = {
  statusCode: string | number;
  errorMessage?: string;
};

export const ErrorComponent = ({ statusCode, errorMessage }: PropsWithChildren<Props>) => {
  const styles = useStyles();
  const intl = useIntl();
  const router = useRouter();
  const tracker = useAnalyticsTracker();
  const rumClient = useAdvancedApmRum();

  const productUrl = useSelector(
    makeAppUrl('/products?go_redirect=disabled&redirect_status=1', 'account')
  );

  const convertErrorCode = (code: number | string) => {
    switch (code) {
      case 403:
      case '403':
        return intl.formatMessage({ id: IntlKeys.DONT_HAVE_ACCESS_TO_PAGE });
      case 404:
      case '404':
        return intl.formatMessage({ id: IntlKeys.PAGE_NOT_FOUND });
      default:
        return intl.formatMessage({ id: IntlKeys.UNKOWN_ERROR });
    }
  };

  const onClick = () => {
    tracker.track(EVENT_KEYS.ERROR_RETRY_BUTTON_CLICK);
    window.location.reload();
  };

  useEffect(() => {
    tracker.track(EVENT_KEYS.ERROR_PAGE_IMPRESSION, { statusCode });
  }, []);

  useEffect(() => {
    rumClient.captureError(new Error(errorMessage), { page: 'Error Page', statusCode });
  }, [errorMessage, statusCode, rumClient]);

  return (
    <div className={classnames('home', styles.home)}>
      <PageTitle titleKey={IntlKeys.PAGE_TITLE_ERROR} />
      <div className={classnames('text-black bg-white', styles.container)}>
        {<h1 className='pt-3 pb-3'>{errorMessage ?? convertErrorCode(statusCode)}</h1>}
        {router && router.route && router.route.valid && (
          <Button
            data-aid='retry-error-button'
            design='primary'
            text={intl.formatMessage({ id: IntlKeys.CLICK_TO_RETRY })}
            onClick={onClick}
          />
        )}

        <Button
          className={styles.myProductsLink}
          design='inline'
          text={intl.formatMessage({ id: IntlKeys.MY_PRODUCTS_LINK })}
          href={productUrl}
        />
      </div>
    </div>
  );
};

type InitialProps = {
  res?: any;
  err?: any;
};

ErrorComponent.getInitialProps = ({ res, err }: InitialProps) => {
  const statusCode = res?.statusCode || err?.statusCode || 404;
  return { statusCode };
};

// eslint-disable-next-line import/no-default-export
export default withGasketDataProvider()(
  withAnalyticsTracker()(
    withTheme()(
      withLocale(
        withRoutes({ withGasketData: false })(
          withAdvancedApmRum({ withGasketData: false, sourceApp: 'gum-marketing' })(
            ErrorComponent as ComponentType<unknown>
          ) as ComponentType<unknown>
        ) as ComponentType<unknown>
      ) as ComponentType<unknown>
    ) as ComponentType<unknown>
  )
);
